import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListPageHeader from '@components/shared/ListPageHeader';
import { shippingRateCardsService } from '@services/operator/shippingRateCardsService';
import { formatDateTime } from '@utils/dateTime';

const ShippingRateCardsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await shippingRateCardsService.fetchList();
      if (response.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.messages.error.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (id) => {
    try {
      const response = await shippingRateCardsService.delete(id);
      if (response.success) {
        message.success(t('operator.shipping_rate_cards.list.delete_success'));
        loadData();
      }
    } catch (error) {
      console.error(error);
      message.error(t('operator.shipping_rate_cards.list.delete_error'));
    }
  };

  const columns = [
    {
      title: t('models.shipping_rate_card.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('models.shipping_rate_card.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => formatDateTime(text),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/shipping_rate_cards/${record.id}/edit`)}
          />
          <Popconfirm
            title={t('operator.shipping_rate_cards.list.delete_confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-content">
      <ListPageHeader
        items={[
          'shipping_settings',
          { label: t('operator.menu.shipping_settings.shipping_rate_cards') }
        ]}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/shipping_rate_cards/new')}
          >
            {t('common.button.add')}
          </Button>
        }
      />
      <Table
        loading={loading}
        dataSource={data}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default ShippingRateCardsList;
